import { IkeToken } from '@ikegps/core-api';
import type { JobSummaryResults } from '@ikegps/core-api';
import { observable, makeObservable } from 'mobx';
import { MaterialIcon } from '../../components/icon/icons';
import { MDL } from '../../components/modal/modal.style';
import { T } from '../../i18n';
import { m } from '../../mithril';
import type { MithrilVNode } from '../../mithril';
import { PhotomeasureButton as JobButton } from '../../modules/photomeasure.button/photomeasure.button';
import { Services } from '../../services';
import { IkePage, type IkePageConfig, IkePageName } from '../page';
import { DashBanner } from './dashboard.banner';
import { CSS } from './dashboard.style';
import { Table } from './dashboard.table';
import { CSS as TableCSS } from './dashboard.table.style';

// original status headers
// export enum statusHeaders {
//     'BACKLOG',
//     'NOT COLLECTED',
//     'COLLECTED',
//     'HOA COMPLETE',
//     'PLA COMPLETE',
//     'MRA',
//     'RECOLLECT',
//     'BILLED',
//     'DONE'
// }

//analyst status headers
export enum statusHeaders {
	'PLANNING',
	'COLLECT',
	'ASSIGN',
	'LOCATION',
	'HOA',
	'MEAS QA',
	'PLA',
	'MRE',
	'FINAL QA',
	'DELIVERY',
	'ACCEPTANCE',
	'BILLING',
	'DONE',
	'REWORK',
}

//exec status headers
export enum execHeaders {
	'PLANNING',
	'IKE FIELD',
	'IKE OFFICE',
	'QA',
	'DELIVERY',
	'DONE',
	'REWORK',
}

//this need to be in the same order as the header enums
var headerMap: { [key: string]: string } = {
	PLANNING: 'PLANNING',
	COLLECT: 'IKE FIELD',
	ASSIGN: 'IKE OFFICE',
	LOCATION: 'IKE OFFICE',
	HOA: 'IKE OFFICE',
	'MEAS QA': 'IKE OFFICE',
	PLA: 'IKE OFFICE',
	MRE: 'IKE OFFICE',
	'FINAL QA': 'QA',
	DELIVERY: 'DELIVERY',
	ACCEPTANCE: 'DELIVERY',
	BILLING: 'DELIVERY',
	DONE: 'DONE',
	REWORK: 'REWORK',
};

export const DashPoleStatusConfig: IkePageConfig = {
	type: 'internal',
	name: IkePageName.dashboardPolestatus,
	path: '/dashboard/pole_status',
	auth: {
		required: true,
		tokens: [IkeToken.DASHBOARD_PAGE],
	},
	nav: {
		name: T.DASHBOARD,
		icon: MaterialIcon.DASHBOARD,
	},
	layout: { nav: false, banner: true },
};

export class DashPoleStatus extends IkePage {
	@observable poleStatusSum: JobSummaryResults[] | null = null;
	@observable private execView: boolean = true; //starts off in executive view

	constructor() {
		super(DashPoleStatusConfig);
		makeObservable(this);
	}

	async init() {
		this.poleStatusSum = await Services.Dashboard.getPoleStatusSummary();
	}

	get nodes(): MithrilVNode {
		let node: MithrilVNode | null = null;

		if (!Services.Token.has(IkeToken.DASHBOARD_PAGE)) {
			node = m('div', { className: CSS.container }, m('div', { className: CSS.message }, 'No Dashboard Token'));
			return node;
		}

		if (this.poleStatusSum == null) {
			// eslint-disable-next-line @typescript-eslint/no-floating-promises
			this.init();
		}

		node = m('div', { className: CSS.container }, [DashBanner.nodes, this.viewTable()]);

		return node;
	}

	private toggleView() {
		this.execView = !this.execView;
	}

	public getTableView(): boolean {
		return this.execView;
	}

	@observable private viewTable(): MithrilVNode {
		const rows: MithrilVNode[][] = [];
		let poleStatTotal: number[] = [];
		let execPoleStatTotal: number[] = [];

		//zeroing out the totals aggregation arrays to be the length of the table headers
		//enums aren't easily iterable so they have to become objects first.
		for (let i = 0; i < Object.keys(execHeaders).filter((k) => typeof execHeaders[k as any] === 'number').length; i++) {
			execPoleStatTotal[i] = 0;
		}
		for (
			let i = 0;
			i < Object.keys(statusHeaders).filter((k) => typeof statusHeaders[k as any] === 'number').length;
			i++
		) {
			poleStatTotal[i] = 0;
		}

		//make sure we have data
		if (this.poleStatusSum != null) {
			let index = 0;
			this.poleStatusSum.forEach((el) => {
				if (el != null) {
					let jobName = el.jobName == null ? 'NULL' : el.jobName;
					let jobId = el.jobId == null ? '' : el.jobId;
					if (el.collectionStatusSummary != null) {
						let poleStat: number[] = [];
						let execPoleStat: number[] = [];

						//zeroing out the row aggregation arrays to be the length of the table headers
						for (
							let i = 0;
							i < Object.keys(execHeaders).filter((k) => typeof execHeaders[k as any] === 'number').length;
							i++
						) {
							execPoleStat[i] = 0;
						}
						for (
							let i = 0;
							i < Object.keys(statusHeaders).filter((k) => typeof statusHeaders[k as any] === 'number').length;
							i++
						) {
							poleStat[i] = 0;
						}

						el.collectionStatusSummary.forEach((col) => {
							if (col != null) {
								if (col.collections != null && col.status != null) {
									// enum[enum_string] returns the place of that string in the enum
									// sorts the collection counts into the correct columns for exec and analyst views
									execPoleStat[execHeaders[headerMap[this.cleanStatus(col.status)] as keyof typeof execHeaders]] +=
										col.collections;
									execPoleStatTotal[execHeaders[headerMap[this.cleanStatus(col.status)] as keyof typeof execHeaders]] +=
										col.collections;

									poleStat[statusHeaders[this.cleanStatus(col.status) as keyof typeof statusHeaders]] +=
										col.collections;
									poleStatTotal[statusHeaders[this.cleanStatus(col.status) as keyof typeof statusHeaders]] +=
										col.collections;
								}
							}
						});

						//only show jobs that had at least some non null data
						if (poleStat.reduce((a, b) => a + b, 0) > 0) {
							index++;
							rows.push(this.viewRow(index, jobId, jobName, this.execView ? execPoleStat : poleStat));
						}
					}
				}
			});
			//add the totals row to the top of the table
			rows.unshift(this.viewTotals(this.execView ? execPoleStatTotal : poleStatTotal));
			return Table.view(this.viewTitle(), this.viewHeaders(), rows);
		}
		return m(
			'div',
			{
				className: CSS.message,
			},
			'No Data Available'
		);
	}

	@observable private viewTitle(): MithrilVNode {
		//the title also has a button to switch between views
		return m('div', { className: TableCSS.title }, [
			m('h6', {}, 'POLE STATUS'),
			m(
				'button',
				{
					className: MDL.button,
					onclick: (ev: MouseEvent) => {
						this.toggleView();
					},
				},
				this.execView ? 'EXEC' : 'ANALYST'
			),
		]);
	}

	@observable private viewHeaders() {
		let headers: string[] = [];

		headers.push('#');
		headers.push('JOB NAME');

		if (this.execView) {
			const execH = Object.keys(execHeaders).filter((k) => typeof execHeaders[k as any] === 'number');
			for (let i = 0; i < execH.length; i++) {
				headers.push(execH[i]);
			}
		} else {
			const statH = Object.keys(statusHeaders).filter((k) => typeof statusHeaders[k as any] === 'number');
			for (let i = 0; i < statH.length; i++) {
				headers.push(statH[i]);
			}
		}
		headers.push('TOTAL');
		return headers;
	}

	private viewTotals(totals: number[]): MithrilVNode[] {
		let columns: MithrilVNode[] = [];
		columns.push(m('span', { className: CSS.tableCell }, '-'));
		columns.push(m('span', { className: CSS.tableCell }, 'TOTALS:'));
		for (let i = 0; i < totals.length; i++) {
			columns.push(
				m(
					'span',
					{
						className: this.execView
							? 'execHeader' + (i % 2)
							: 'execHeader' + (execHeaders[headerMap[statusHeaders[i]] as keyof typeof execHeaders] % 2),
					},
					totals[i] ? totals[i] : 0
				)
			);
		}
		columns.push(
			m(
				'span',
				{ className: CSS.tableCell },
				totals.reduce((a, b) => a + b, 0)
			)
		);

		return columns;
	}

	@observable private viewRow(index: number, id: string, name: string, cols: number[]): MithrilVNode[] {
		let columns: MithrilVNode[] = [];
		columns.push(m('span', { className: CSS.tableCell }, index));

		columns.push(
			m('div', { className: CSS.tableCellRoleActions }, [
				m('div', { className: CSS.tableCell }, name),
				m('div', { className: CSS.tableCellGhost }, JobButton.view(id)),
			])
		); //jobname
		//iterates through the columns
		for (let i = 0; i < cols.length; i++) {
			columns.push(
				m(
					'span',
					{
						className: this.execView
							? 'execHeader' + (i % 2)
							: 'execHeader' + (execHeaders[headerMap[statusHeaders[i]] as keyof typeof execHeaders] % 2),
					},
					cols[i] ? cols[i] : 0
				)
			);
		}
		//adds up everything for the total column
		columns.push(
			m(
				'span',
				{ className: CSS.tableCell },
				cols.reduce((a, b) => a + b, 0)
			)
		);
		return columns;
	}

	private cleanStatus(status: string): string {
		if (status != null) {
			const cleanStat = status.toUpperCase().trim();
			return cleanStat;
		}

		return '';
	}
}
